import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { SearchModel, SearchResultModel } from "src/common/components";
import { environment } from "src/environments/environment";
import { AssessmentAreaBasicModel } from "../assessment-areas";
import { MessageSearchModel } from "../messages";
import { OpnBoardMembersListModel } from "./opn-steps/opn-boardmembers-list";
import { OpnOfferEvaluationMethodModel } from "./opn-steps/opn-offer-evaluation-methods";
import { OpnProjectDeadlineListViewModel } from "./opn-steps/opn-projectdeadlines-list";
import { OpnSupplierListModel } from "./opn-steps/opn-suppliers-list";
import { OpnTeamMemberModel } from "./opn-steps/opn-team-members";
import { RequestModel, CheckSendOpnForConfirmationModel, RequestSendModel, RequestMessageSendModel, RequestMessageModel, RequestDetailsModel, RequestSaveModel, RequestStatusUniqueModel, RequestDeadlineModel, RequestStatusModel, OpnIntroductionModel, RequestOpnConsentModel, RequestOpnConsentListModel } from "./request.model";

const localApiUrl = `${environment.apiUrl}/Requests`;

@Injectable()
export class RequestsService {

  constructor(private http: HttpClient) { }

  search(search: SearchModel): Promise<SearchResultModel<RequestModel>> {
    return this.http.post<SearchResultModel<RequestModel>>(`${localApiUrl}/Search`, search).toPromise();
  }

  searchTeamMembers(search: SearchModel): Promise<SearchResultModel<RequestModel>> {
    return this.http.post<SearchResultModel<RequestModel>>(`${localApiUrl}/SearchTeamMembers`, search).toPromise();
  }

  searchCheckSendOpnForConfirmationView(requestItemsId: number[]): Promise<CheckSendOpnForConfirmationModel[]> {
    return this.http.post<CheckSendOpnForConfirmationModel[]>(`${localApiUrl}/SearchCheckSendOpnForConfirmation`, requestItemsId).toPromise();
  }

  checkSendOpnForConfirmationId(id: number): Promise<CheckSendOpnForConfirmationModel> {
    return this.http.post<CheckSendOpnForConfirmationModel>(`${localApiUrl}/CheckSendOpnForConfirmationId/${id}`, '').toPromise();
  }

  getOpnDocument(id: number): Promise<number> {
    return this.http.get<number>(`${localApiUrl}/OpnDocument/${id}`).toPromise();
  }

  changeRequestOpnStatusApproved(requestSendModel): Promise<RequestSendModel> {
    return this.http.post<RequestSendModel>(`${localApiUrl}/ChangeRequestOpnStatusApproved`, requestSendModel).toPromise();
  }

  changeRequestOpnStatusRejected(requestMessageSendModel): Promise<RequestMessageSendModel> {
    return this.http.post<RequestMessageSendModel>(`${localApiUrl}/ChangeRequestOpnStatusRejected`, requestMessageSendModel).toPromise();
  }

  getAll(): Promise<RequestModel[]> {
    return this.http.get<RequestModel[]>(`${localApiUrl}/All`).toPromise();
  }

  messagesSearch(search: SearchModel, id: number): Promise<SearchResultModel<RequestMessageModel>> {
    return this.http.post<SearchResultModel<RequestMessageModel>>(`${localApiUrl}/MessagesSearch/${id}`, search).toPromise();
  }

  getMessages(id: number, model: MessageSearchModel): Promise<RequestMessageModel[]> {
    return this.http.post<RequestMessageModel[]>(`${localApiUrl}/Messages/${id}`, model).toPromise();
  }

  sendMessage(model: RequestMessageSendModel): Promise<void> {
    return this.http.post<void>(`${localApiUrl}/SendMessage`, model).toPromise();
  }

  getById(id: number): Promise<RequestDetailsModel> {
    return this.http.get<RequestDetailsModel>(`${localApiUrl}/Details/${id}`).toPromise();
  }

  delete(id: number): Promise<void> {
    return this.http.delete<void>(`${localApiUrl}/Delete/${id}`).toPromise();
  }

  save(model: RequestSaveModel): Promise<void> {
    return this.http.post<void>(`${localApiUrl}/Save`, model).toPromise();
  }

  getRequestStatusUnique(id: number): Promise<RequestStatusUniqueModel> {
    return this.http.get<RequestStatusUniqueModel>(`${localApiUrl}/RequestStatusUnique/${id}`).toPromise();
  }
  deadlineForCompletion(id: number): Promise<RequestDeadlineModel> {
    return this.http.get<RequestDeadlineModel>(`${localApiUrl}/DeadlineForCompletion/${id}`).toPromise();
  }

  getRequestStatusesFilter(): Promise<RequestStatusModel[]> {
    return this.http.get<RequestStatusModel[]>(`${localApiUrl}/RequestStatusesSearch`).toPromise();
  }

  getRequestSubjectById(id: number): Promise<OpnIntroductionModel> {
    return this.http.get<OpnIntroductionModel>(`${localApiUrl}/RequestSubjectById/${id}`).toPromise();
  }

  opnOfferEvaluationMethodsSearch(search: SearchModel, id: number): Promise<SearchResultModel<OpnOfferEvaluationMethodModel>> {
    return this.http.post<SearchResultModel<OpnOfferEvaluationMethodModel>>(`${localApiUrl}/OpnOfferEvaluationMethodsSearch/${id}`, search).toPromise();
  }

  opnOfferEvaluationMethodsGetAll(id: number): Promise<OpnOfferEvaluationMethodModel[]> {
    return this.http.get<OpnOfferEvaluationMethodModel[]>(`${localApiUrl}/OpnOfferEvaluationMethodsAll/${id}`).toPromise();
  }

  opnTeamMembersSearch(search: SearchModel): Promise<SearchResultModel<OpnTeamMemberModel>> {
    return this.http.post<SearchResultModel<OpnTeamMemberModel>>(`${localApiUrl}/OpnTeamMembersSearch`, search).toPromise();
  }

  opnTeamMembersGetAllInitial(id: number): Promise<OpnTeamMemberModel[]> {
    return this.http.get<OpnTeamMemberModel[]>(`${localApiUrl}/OpnTeamMembersAllInitial/${id}`).toPromise();
  }

  opnTeamMembersGetAll(id: number): Promise<OpnTeamMemberModel[]> {
    return this.http.get<OpnTeamMemberModel[]>(`${localApiUrl}/OpnTeamMembersAll/${id}`).toPromise();
  }

  getRequestAssessmentAreas(id: number): Promise<AssessmentAreaBasicModel[]> {
    return this.http.get<AssessmentAreaBasicModel[]>(`${localApiUrl}/RequestAssessmentAreas/${id}`).toPromise();
  }

  opnSuppliersListSearch(search: SearchModel, id: number): Promise<SearchResultModel<OpnSupplierListModel>> {
    return this.http.post<SearchResultModel<OpnSupplierListModel>>(`${localApiUrl}/OpnSuppliersListSearch/${id}`, search).toPromise();
  }

  opnSuppliersListGetAll(id: number): Promise<OpnSupplierListModel[]> {
    return this.http.get<OpnSupplierListModel[]>(`${localApiUrl}/OpnSuppliersListAll/${id}`).toPromise();
  }

  opnBoardMembersListSearch(search: SearchModel, id: number): Promise<SearchResultModel<OpnBoardMembersListModel>> {
    return this.http.post<SearchResultModel<OpnBoardMembersListModel>>(`${localApiUrl}/OpnBoardMembersListSearch/${id}`, search).toPromise();
  }

  opnBoardMembersListGetAll(id: number): Promise<OpnBoardMembersListModel[]> {
    return this.http.get<OpnBoardMembersListModel[]>(`${localApiUrl}/OpnBoardMembersListAll/${id}`).toPromise();
  }

  opnProjectDeadlinesListSearch(search: SearchModel, id: number): Promise<SearchResultModel<OpnProjectDeadlineListViewModel>> {
    return this.http.post<SearchResultModel<OpnProjectDeadlineListViewModel>>(`${localApiUrl}/OpnProjectDeadlineListSearch/${id}`, search).toPromise();
  }

  opnProjectDeadlinesListGetAll(id: number): Promise<OpnProjectDeadlineListViewModel[]> {
    return this.http.get<OpnProjectDeadlineListViewModel[]>(`${localApiUrl}/OpnProjectDeadlineListAll/${id}`).toPromise();
  }

  setOpnConsentStatus(model: RequestOpnConsentModel): Promise<void> {
    return this.http.post<void>(`${localApiUrl}/OpnSetConsentStatus`, model).toPromise();
  }

  getOpnConsents(id: number): Promise<RequestOpnConsentListModel[]> {
    return this.http.get<RequestOpnConsentListModel[]>(`${localApiUrl}/OpnConsents/${id}`).toPromise();
  }

  allTeamMembersGaveTheirOpnConsent(id: number): Promise<boolean> {
    return this.http.get<boolean>(`${localApiUrl}/OpnAllTeamMembersGaveTheirConsent/${id}`).toPromise();
  }

  isBoardMember(id: number): Promise<boolean> {
    return this.http.get<boolean>(`${localApiUrl}/IsBoardMember/${id}`).toPromise();
  }
}
